import { z } from 'zod';

export const widgetTextSchema = z.object({
    type: z.literal('text'),
});
export type WidgetText = z.infer<typeof widgetTextSchema>;
export const widgetTextValueSchema = z.string();

export const widgetTextareaSchema = z.object({
    type: z.literal('textarea'),
    params: z
        .object({
            class: z.literal('mceEditor'),
            mention: z.literal('1'),
        })
        .optional(),
});
export type WidgetTextarea = z.infer<typeof widgetTextareaSchema>;

export const widgetTextareaValueSchema = z.string().nullable();

export const widgetNumberSchema = z.object({
    type: z.literal('number'),
});
export type WidgetNumber = z.infer<typeof widgetNumberSchema>;
export const widgetNumberValueSchema = z.number();

export const widgetColorSchema = z.object({
    type: z.literal('color'),
    options: z.object({
        colors: z.string(),
        defaultColor: z.string(),
    }),
});
export type WidgetColor = z.infer<typeof widgetColorSchema>;

export const widgetDateSchema = z.object({
    type: z.literal('date'),
    format: z.string(),
});
export type WidgetDate = z.infer<typeof widgetDateSchema>;
export const widgetDateValueSchema = z.string().nullable();

export const widgetDatetimeSchema = z.object({
    type: z.literal('datetime'),
});
export type WidgetDatetime = z.infer<typeof widgetDatetimeSchema>;
export const widgetDatetimeValueSchema = z.string().nullable();

export const widgetDatePickerSchema = z.object({
    type: z.literal('datePicker'),
});
export type WidgetDatePicker = z.infer<typeof widgetDatePickerSchema>;
export const widgetDatePickerValueSchema = z.string().nullable();

export const widgetActiveValueSchema = z.number();

export const widgetAudienceSchema = z.object({
    type: z.literal('audience'),
    options: z
        .object({
            disableEveryoneAudience: z.boolean().optional(),
            views: z.enum(['folder', 'user', 'group', 'businessContact']).array().optional(),
        })
        .optional(),
});
export type WidgetAudience = z.infer<typeof widgetAudienceSchema>;

export const widgetLinkSchema = z.object({
    type: z.literal('link'),
});
export type WidgetLink = z.infer<typeof widgetLinkSchema>;

export const widgetMultiattributeSchema = z.object({
    type: z.literal('multiattribute'),
});
export type WidgetMultiattribute = z.infer<typeof widgetMultiattributeSchema>;

export const widgetFileSchema = z.object({
    type: z.literal('file'),
});
export type WidgetFile = z.infer<typeof widgetFileSchema>;

export const widgetImageSchema = z.object({
    type: z.literal('image'),
    params: z.optional(
        z.object({
            avatar: z.optional(z.boolean()),
        }),
    ),
});
export type WidgetImage = z.infer<typeof widgetImageSchema>;

export const widgetSelectSchema = z.object({
    type: z.literal('select'),
    options: z.record(z.string()),
    params: z
        .object({
            data: z.string().optional(),
            defaultValue: z.string().optional(),
            explain: z.literal(1).optional(),
        })
        .optional(),
});
export type WidgetSelect = z.infer<typeof widgetSelectSchema>;

export const widgetEmbedSchema = z.object({
    type: z.literal('embed'),
});
export type WidgetEmbed = z.infer<typeof widgetEmbedSchema>;

export const widgetEmailSchema = z.object({
    type: z.literal('email'),
});
export type WidgetEmail = z.infer<typeof widgetEmailSchema>;
export const widgetEmailValueSchema = z.string();

export const widgetUrlSchema = z.object({
    type: z.literal('url'),
    params: z.object({
        target: z.literal('_blank').optional(),
    }),
});
export type WidgetUrl = z.infer<typeof widgetUrlSchema>;
export const widgetUrlValueSchema = z.string();

export const widgetPlaceSchema = z.object({
    type: z.literal('place'),
    options: z.record(z.string()),
});
export type WidgetPlace = z.infer<typeof widgetPlaceSchema>;

export const widgetRefUserSchema = z.object({
    type: z.literal('refUser'),
});
export type WidgetRefUser = z.infer<typeof widgetRefUserSchema>;

export const widgetTransientSchema = z.object({
    type: z.literal('transient'),
});
export type WidgetTransient = z.infer<typeof widgetTransientSchema>;

export const widgetRadioSchema = z.object({
    type: z.literal('radio'),
    options: z.record(z.string()),
});
export type WidgetRadio = z.infer<typeof widgetRadioSchema>;

export const widgetCheckboxSchema = z.object({
    type: z.literal('checkbox'),
    options: z.record(z.string()),
});
export type WidgetCheckbox = z.infer<typeof widgetCheckboxSchema>;

export const widgetTaxonomySchema = z.object({
    type: z.literal('taxonomy'),
    params: z.object({
        idTaxonomy: z.string(),
        type: z.string().optional(),
        limit: z.number().optional(),
    }),
});
export type WidgetTaxonomy = z.infer<typeof widgetTaxonomySchema>;

export const widgetTelSchema = z.object({
    type: z.literal('tel'),
});
export type WidgetTel = z.infer<typeof widgetTelSchema>;

export const widgetToggleSchema = z.object({
    type: z.literal('toggle'),
});
export type WidgetToggle = z.infer<typeof widgetToggleSchema>;

export const widgetOrientedlinksSchema = z.object({
    type: z.literal('orientedlinks'),
    params: z.object({
        'jcomplete-multiple': z.union([z.literal('0'), z.literal('1')]),
        showAdd: z.union([z.literal('0'), z.literal('1')]),
        supportedTypes: z.array(z.string()),
    }),
});
export type WidgetOrientedlinks = z.infer<typeof widgetOrientedlinksSchema>;

export const widgetUriSchema = z.object({
    type: z.literal('uri'),
    params: z.object({
        class: z.string(),
        'jcomplete-url': z.string(),
        type: z.string(),
    }),
});
export type WidgetUri = z.infer<typeof widgetUriSchema>;

export const widgetSchema = z.union([
    widgetTextSchema,
    widgetTextareaSchema,
    widgetNumberSchema,
    widgetDateSchema,
    widgetDatetimeSchema,
    widgetDatePickerSchema,
    widgetSelectSchema,
    widgetEmbedSchema,
    widgetEmailSchema,
    widgetUrlSchema,
    widgetRefUserSchema,
    widgetRadioSchema,
    widgetCheckboxSchema,
    widgetOrientedlinksSchema,
    widgetTaxonomySchema,
    widgetUriSchema,
    widgetTransientSchema,
    widgetMultiattributeSchema,
    widgetTelSchema,
    widgetToggleSchema,
    widgetPlaceSchema,
    widgetColorSchema,
    widgetLinkSchema,
    widgetFileSchema,
    widgetImageSchema,
    widgetAudienceSchema,
]);
export type Widget = z.infer<typeof widgetSchema>;

export const widgetsValueSchema = z.union([
    widgetActiveValueSchema,
    widgetDatePickerValueSchema,
    widgetDatetimeValueSchema,
    widgetDateValueSchema,
    widgetEmailValueSchema,
    widgetNumberValueSchema,
    widgetTextareaValueSchema,
    widgetTextValueSchema,
    widgetUrlValueSchema,
]);
export type WidgetsValue = z.infer<typeof widgetsValueSchema>;

export type FieldConfiguration<T extends string = string> = {
    label: string;
    description?: string;
    widget: Widget;
    name: T;
    mandatory: boolean;
};

export type Configuration<T extends string> = FieldConfiguration<T>;

export type Model<T extends string = string> = {
    type: string;
    mainType: string;
    label: string;
    fields: Record<T, Configuration<T>>;
    icon: string;
    color: string;
};
